// Streetwear Raw — Social Post design #8.
//
// Ported from the Claude Design "Fashion - Social Post" bundle
// (fashion-posts.jsx PostStreet). Renders at fixed 1080×1080.
//
// Visual: near-black grid canvas, orange tape strips, monospace meta
// codes, Anton display headline with outlined em phrase, barcode under
// the product. Heavily decorative — many small codes (REF, LOT, SKU,
// PHOTO/35MM) are intentionally hardcoded as visual flair, not slots.
//
// Slot mapping for the user-editable bits:
//   - brand_name → masthead (rendered with a slash accent inserted)
//   - eyebrow → top "// EYEBROW" tape strip
//   - issue_label → bottom tape "// DROP · LIMITED" line

import type { SocialPostSlots } from '@/lib/api';
import './PostStreet.css';

interface PostStreetProps {
  slots: SocialPostSlots;
  productImageUrl: string;
}

// Decorative barcode bars — variable widths, fixed sequence for visual
// consistency across renders. Source design uses the same array.
const BARCODE_BARS = [3, 1, 2, 4, 1, 3, 2, 1, 4, 2, 3, 1, 2, 4, 2, 1, 3, 2];

export default function PostStreet({ slots, productImageUrl }: PostStreetProps) {
  // Brand masthead — uppercase and insert a slash midway if the name has
  // two words ("MAISON / ATELIER"); otherwise render whole.
  const brandRaw = (slots.brand_name || '').trim().toUpperCase();
  const brandHalves = brandRaw.split(/\s+/);
  const brandLeft = brandHalves[0] || '';
  const brandRight = brandHalves.slice(1).join(' ');

  return (
    <div className="post-street">
      <div className="frame-pad">
        <div className="tape t1">// {slots.eyebrow}</div>
        <div className="tape t2">{slots.issue_label || ''}</div>
        <div className="top">
          <div className="brand">
            {brandLeft}{brandRight ? <span className="slash">/</span> : null}{brandRight}
          </div>
          <div className="meta-line">
            REF · MA-2604-FW<br />
            LOT · <span>0001 / 0500</span>
          </div>
        </div>
        <div className="core">
          <div className="left">
            <div>
              <div className="eyebrow">CHAPTER 04 · OUTERWEAR</div>
              <h1 dangerouslySetInnerHTML={{ __html: slots.headline }} />
              <div className="sub">{slots.subhead}</div>
            </div>
            <ul className="bullets">
              <li><span className="n">01</span><span>{slots.bullet1}</span><span className="tag">spec</span></li>
              <li><span className="n">02</span><span>{slots.bullet2}</span><span className="tag">spec</span></li>
              <li><span className="n">03</span><span>{slots.bullet3}</span><span className="tag">spec</span></li>
            </ul>
          </div>
          <div className="right">
            <div className="product">
              {productImageUrl ? (
                <img src={productImageUrl} alt="Product" />
              ) : (
                <div className="empty-state">Product image</div>
              )}
            </div>
            <div className="barcode">
              <div className="lines">
                {BARCODE_BARS.map((w, i) => <i key={i} style={{ width: `${w}px` }} />)}
              </div>
              <span>SKU · 00451-FW</span>
            </div>
          </div>
        </div>
        <div className="bot">
          <div className="handle">→ <span>{slots.handle}</span></div>
          <div className="badge">
            <span className="stars">{slots.badge_stars}</span>
            <span>{slots.badge_text}</span>
          </div>
          <div className="cta">{slots.cta} <span className="arr">↗</span></div>
        </div>
      </div>
    </div>
  );
}
